import { createContext, useCallback, useContext, useEffect } from 'react';
import { Link as RouterLink, NavLink as RouterNavLink, Navigate as RouterNavigate, useLocation, useNavigate } from 'react-router-dom';
import italian from './it.json';

export function stripLanguage(path) { return path.replace(/^\/it(?=\/|$)/, '') || '/'; }
export function localizeUrl(value, language) {
  if (typeof value !== 'string' || !value || value.startsWith('#')) return value;
  if (/^(mailto:|tel:)/.test(value)) return value;
  if (/^https?:/.test(value)) {
    const url = new URL(value);
    if (!['liminalityhub.com', 'apps.liminalityhub.com'].includes(url.hostname)) return value;
    url.pathname = (language === 'it' ? '/it' : '') + stripLanguage(url.pathname);
    return url.toString();
  }
  if (!value.startsWith('/')) return value;
  return (language === 'it' ? '/it' : '') + stripLanguage(value);
}
const identity = value => value;
const LanguageContext = createContext({ language: 'en', t: identity });
export function LanguageProvider({ children }) {
  const { pathname } = useLocation();
  const language = /^\/it(?:\/|$)/.test(pathname) ? 'it' : 'en';
  const t = useCallback(value => language === 'it' ? (italian[value] || value) : value, [language]);
  useEffect(() => {
    document.documentElement.lang = language;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.content = language === 'it'
      ? 'Siamo Luca e Michele. Progettiamo e sviluppiamo siti e app su misura per le aziende, dalla prima conversazione al lancio.'
      : 'Luca and Michele design and build websites and custom apps for businesses, from the first conversation to launch.';
    for (const code of ['en', 'it']) {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${code}"]`);
      if (!link) { link = document.createElement('link'); link.rel = 'alternate'; link.hreflang = code; document.head.appendChild(link); }
      link.href = new URL(localizeUrl(pathname, code), window.location.origin).href;
    }
  }, [language, pathname]);
  return <LanguageContext.Provider value={{ language, t }}>{children}</LanguageContext.Provider>;
}
export const useLanguage = () => useContext(LanguageContext);
export function Link({ to, ...props }) { const { language } = useLanguage(); return <RouterLink to={localizeUrl(to, language)} {...props} />; }
export function NavLink({ to, ...props }) { const { language } = useLanguage(); return <RouterNavLink to={localizeUrl(to, language)} {...props} />; }
export function Navigate({ to, ...props }) { const { language } = useLanguage(); return <RouterNavigate to={localizeUrl(to, language)} {...props} />; }
export function useLocalizedNavigate() {
  const navigate = useNavigate(); const { language } = useLanguage();
  return useCallback((to, options) => navigate(localizeUrl(to, language), options), [navigate, language]);
}
export function LanguageSwitch() {
  const { pathname, search, hash } = useLocation(); const { language, t } = useLanguage();
  return <div className="language-switch" role="group" aria-label={t('Choose language')}>
    <RouterLink to={localizeUrl(pathname, 'en') + search + hash} lang="en" hrefLang="en" aria-label="English" aria-current={language === 'en' ? 'true' : undefined}>EN</RouterLink>
    <RouterLink to={localizeUrl(pathname, 'it') + search + hash} lang="it" hrefLang="it" aria-label="Italiano" aria-current={language === 'it' ? 'true' : undefined}>IT</RouterLink>
  </div>;
}

export function CopyText({ source }) {
  const { t } = useLanguage();
  return <>{t(source)}</>;
}
